import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../components/Shell/Modal';
import useModal from '../../hooks/useModal';
import {
	CheckCircleIcon,
	ChevronRightIcon,
	ClockIcon,
	DocumentCheckIcon,
	EllipsisVerticalIcon,
	MinusCircleIcon,
	PlusCircleIcon,
	PlusIcon,
	WrenchScrewdriverIcon,
	XCircleIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';
import {
	TemplateBoolean,
	TemplateCheckbox,
	TemplateContact,
	TemplateCreateable,
	TemplateDate,
	TemplateDropdown,
	TemplateField,
	TemplateReactSelect,
	TemplateTeamMembers,
} from '../../components/Attributes/Attributes_old';
import FileUpload from '../../components/nextgen/FileUpload';
import {
	AssetSelectorBeta,
	SelectedAssetsList,
	VendorSelectorBeta,
} from '../../components/nextgen';
import usePrevious from '../../hooks/usePrevious';
import useAsync from '../../hooks/useAsync';
import {
	getModuleCategories,
	getModuleFieldsBeta,
	updateModuleFieldActive,
} from '../../api/modules';
import Button from '../../components/Button';
import Attributes, { configurePayload } from '../../components/Attributes';
import LoadingSkeleton from '../../components/Shell/LoadingSkeleton';
import { FIELD_OPTIONS } from '../../components/helpers/constants';
import { capitalize } from 'lodash';
import {
	capitalizeFirstLetter,
	classNames,
	getUserFullName,
} from '../../components/helpers/functions';
import { useRequiredBeta } from '../../hooks/useRequired';
import PDFModal from '../PDFModal';
import { addContract } from '../../api';
import { getAssets, getAsset } from '../../api/assets';
import { getContacts } from '../../api/contacts';
import { getPMSchedules } from '../../api/pmSchedules';
import moment from 'moment';
import { NOTIFY_OPTIONS } from '../../constants';
import { AvatarGenerated } from '../../components/Avatar';
import { getTeam } from '../../api/company';
import ContractPMSchedules from './ContractPMSchedules';
import { Menu } from '@headlessui/react';
import GeneralStepContract from './steps/GeneralStepContract';
import AttributesStepContract from './steps/AttributesStepContract';
import { useNavigate } from 'react-router-dom';

const MODAL_NAME = 'contractAdd';
const DEFAULT_FORM = {};
const CONTRACT_MODULE_ID = process.env.REACT_APP_MODULE_ID_CONTRACT;

const DEFAULT_NOTIFICATION_FORM = { time: 30 };

const STATUSES = {
	complete: 'text-green-700 bg-green-50 ring-green-600/20',
	default: 'text-gray-600 bg-gray-50 ring-gray-500/10',
	pending: 'text-yellow-800 bg-yellow-50 ring-yellow-600/20',
};

const AddContractBetaModal = () => {
	const navigate = useNavigate();
	const [currentContractStep, setCurrentContractStep] = useState(0);
	const [form, setForm] = useState({});
	const [updatedForm, setUpdateForm] = useState({});
	const [pmSchedulesForm, setPMSchedulesForm] = useState([]);
	const [notificationForm, setNotificationForm] = useState(
		DEFAULT_NOTIFICATION_FORM,
	);
	const [customAttributeForm, setCustomAttributeForm] = useState({});
	const [acceptedFiles, setAcceptedFiles] = useState(null);
	const [selectedAssetIds, setSelectedAssetIds] = useState([]);
	const [selectedVendorId, setSelectedVendorId] = useState(null);

	// Track which items were auto-added from asset/PM schedule relationships
	const [autoAddedSections, setAutoAddedSections] = useState({
		pmSchedules: false,
		vendor: false,
		assets: false,
	});

	const { value: assets } = useAsync(getAssets);
	const { value: contacts } = useAsync(getContacts);
	const { value: allPMSchedules } = useAsync(getPMSchedules, {
		defaultPayload: { pageSize: 100 },
	});

	// Fetch single asset details when asset is selected
	const { execute: fetchAssetDetails } = useAsync(getAsset, {
		immediate: false,
	});

	// Process assets data for selector
	const processedAssets = useMemo(() => {
		if (!assets?.rows) return [];
		return assets.rows.map((asset) => ({
			id: asset.id,
			name:
				[
					asset.manufacturer?.name || asset.manufacturerName,
					asset.model?.name || asset.modelName,
				]
					.filter(Boolean)
					.join(' ') || 'Unnamed Asset',
			manufacturer:
				asset.manufacturer?.name || asset.manufacturerName || 'Unknown',
			model: asset.model?.name || asset.modelName || '',
			type: asset.model?.name || asset.modelName || 'Unknown Type',
			location: asset.location?.name || asset.locationName || 'No Location',
			serial: asset.serialNumber || 'N/A',
		}));
	}, [assets]);

	const { open: manageAttributesSettings, setOpen: openManageAttributes } =
		useModal('manageAttributes');

	const [isAddCustomAttributesModalOpen, setAddCustomAttributesModal] =
		useState(false);

	const { open: options, setClose } = useModal(MODAL_NAME);
	const {
		open: isPDFModalOpen,
		setClose: onCloseUploadContractPdfModal,
		setOpen: onOpenUploadContractPdfModal,
	} = useModal('uploadContractPdfModal');

	const [currentStep, setStep] = useState(1);
	const { open: addCategoryOpen, setOpen: setAddCategoryOpen } =
		useModal('addCategoryModal');

	const {
		value: moduleFields,
		status: isLoadingModuleFields,
		execute: onRefreshModuleFields,
	} = useAsync(getModuleFieldsBeta, {
		defaultPayload: { id: CONTRACT_MODULE_ID, active: true },
	});

	const { execute: onSetFieldActive } = useAsync(updateModuleFieldActive, {
		immediate: false,
	});

	const {
		value: categories,
		execute: onRefreshCategories,
		status: isLoadingCategories,
	} = useAsync(getModuleCategories, {
		immediate: false,
	});

	const { execute: handleAddContract, status: addingContract } = useAsync(
		addContract,
		{ immediate: false },
	);

	const { value: teamMembers } = useAsync(getTeam);

	const prevManageAttributes = usePrevious(manageAttributesSettings);
	useEffect(() => {
		if (!manageAttributesSettings && prevManageAttributes) {
			onRefreshModuleFields(
				{ categoryId: form?.category?.value?.value },
				null,
				{ silent: true },
			);
		}
	}, [manageAttributesSettings]);

	const previousCategory = usePrevious(form?.category?.value?.value);
	useEffect(() => {
		if (
			form?.category?.value?.value &&
			previousCategory !== form?.category?.value?.value
		) {
			onRefreshModuleFields(
				{ categoryId: form?.category?.value?.value },
				null,
				{ silent: true },
			);
		}
	}, [form?.category?.value?.value]);

	const prevOptions = usePrevious(options);
	useEffect(() => {
		if (options && !prevOptions) {
			onRefreshCategories({
				id: CONTRACT_MODULE_ID,
			});

			if (options?.assetId) {
				setUpdateForm({
					...updatedForm,
					assets: { value: [{ value: options?.assetId }] },
				});
				// Also set selectedAssetIds for AssetSelectorBeta
				setSelectedAssetIds([options.assetId]);
			}
		}

		if (!options && prevOptions) {
			setForm({});
			setUpdateForm({});
			setStep(1);
			setCurrentContractStep(0);
			setPMSchedulesForm([]);
			setNotificationForm(DEFAULT_NOTIFICATION_FORM);
			setSelectedAssetIds([]);
			setSelectedVendorId(null);
			setAutoAddedSections({
				pmSchedules: false,
				vendor: false,
				assets: false,
			});
		}
	}, [options]);

	const previousCoverageStart = usePrevious(
		updatedForm['coverage-start']?.value,
	);
	useEffect(() => {
		if (previousCoverageStart !== updatedForm['coverage-start']) {
			setUpdateForm({
				...updatedForm,
				'coverage-end': {
					...(updatedForm['coverage-end'] || {
						fieldId: moduleFields?.combined?.find(
							(o) => o.key === 'coverage-end' && o.isDefault,
						)?.id,
					}),
					value: moment(updatedForm['coverage-start']?.value)
						.add(1, 'year')
						.subtract(1, 'day')
						.toDate(),
				},
			});
		}
	}, [updatedForm['coverage-start']?.value]);

	// Auto-populate PM schedules and vendor when an asset is selected
	const prevSelectedAssetIds = usePrevious(selectedAssetIds);
	useEffect(() => {
		if (
			selectedAssetIds?.length > 0 &&
			prevSelectedAssetIds?.length !== selectedAssetIds.length &&
			allPMSchedules?.rows
		) {
			// Find PM schedules that include any of the selected assets
			const matchingSchedules = allPMSchedules.rows.filter((schedule) =>
				schedule.directAssets?.some((asset) =>
					selectedAssetIds.includes(asset.id),
				),
			);

			if (matchingSchedules.length > 0 && pmSchedulesForm.length === 0) {
				// Convert to form format and add to pmSchedulesForm
				const schedulesToAdd = matchingSchedules.map((schedule) => ({
					id: schedule.id,
					name: schedule.name,
					description: schedule.description || '',
					categoryId: schedule.categoryId,
					occurrences: schedule.occurrences,
					customDates: schedule.customDates || [],
					startDate: schedule.startDate,
					generateDaysBefore: schedule.generateDaysBefore || 30,
					sopId: schedule.sopId,
					serviceReportRequired: schedule.serviceReportRequired || false,
					isExisting: true,
				}));
				setPMSchedulesForm(schedulesToAdd);
				setAutoAddedSections((prev) => ({ ...prev, pmSchedules: true }));
			}

			// Fetch first selected asset to get its vendor
			if (selectedAssetIds.length > 0 && !selectedVendorId) {
				fetchAssetDetails({ id: selectedAssetIds[0] }, (assetData) => {
					if (assetData?.contacts?.length > 0) {
						// Get the first vendor contact
						const vendorContact = assetData.contacts.find(
							(c) => c.vendorType === 'service_vendor',
						) || assetData.contacts[0];
						if (vendorContact) {
							setSelectedVendorId(vendorContact.id);
							setAutoAddedSections((prev) => ({ ...prev, vendor: true }));
						}
					}
				});
			}
		}
	}, [selectedAssetIds, allPMSchedules]);

	// Auto-populate assets and vendor when a PM schedule is added that has directAssets
	const prevPMSchedulesForm = usePrevious(pmSchedulesForm);
	useEffect(() => {
		if (
			pmSchedulesForm?.length > 0 &&
			prevPMSchedulesForm?.length !== pmSchedulesForm.length
		) {
			// Check if any added PM schedule is an existing one with directAssets
			const existingSchedules = pmSchedulesForm.filter((s) => s.isExisting);
			if (existingSchedules.length > 0) {
				// Find the full schedule data from allPMSchedules
				const fullSchedules = existingSchedules
					.map((s) => allPMSchedules?.rows?.find((ps) => ps.id === s.id))
					.filter(Boolean);

				// Collect asset IDs from directAssets
				const assetIdsFromSchedules = fullSchedules
					.flatMap((s) => s.directAssets?.map((a) => a.id) || [])
					.filter((id, idx, arr) => arr.indexOf(id) === idx);

				if (assetIdsFromSchedules.length > 0) {
					const newAssetIds = assetIdsFromSchedules.filter(
						(id) => !selectedAssetIds.includes(id),
					);
					if (newAssetIds.length > 0) {
						setSelectedAssetIds([...selectedAssetIds, ...newAssetIds]);
						setAutoAddedSections((prev) => ({ ...prev, assets: true }));
					}
				}

				// Check if PM schedule's contract has a vendor
				const contractWithVendor = fullSchedules.find(
					(s) => s.contract?.contacts?.length > 0,
				);
				if (contractWithVendor && !selectedVendorId) {
					const vendor = contractWithVendor.contract.contacts[0];
					setSelectedVendorId(vendor.id);
					setAutoAddedSections((prev) => ({ ...prev, vendor: true }));
				}
			}
		}
	}, [pmSchedulesForm, allPMSchedules]);

	const SERVICE_CONTRACT_CATEGORY = categories?.find(
		(category) =>
			category?.key === 'default:servicecontract' && category.isDefault,
	);

	const WARRANTY_CONTRACT_CATEGORY = categories?.find(
		(category) => category?.key === 'default:warranty' && category.isDefault,
	);

	const CATEGORY_FIELD_ID = moduleFields?.combined?.find(
		(o) => o.key === 'category' && o.isDefault,
	)?.id;

	const id = null;
	const skipModuleKeys = [
		...(id ? ['coverage-start', 'duration'] : ['category']),
		...(options?.assetId ? ['assets'] : []),
	];

	const hasAllRequired = useRequiredBeta(
		{ ...form, ...updatedForm },
		moduleFields?.combined
			?.filter((field) => field.isRequired)
			.map((field) => field.key),
	);

	const onAddContract = ({ files = [] }) => {
		console.log(
			'onAddContract - pmSchedulesForm:',
			JSON.stringify(pmSchedulesForm, null, 2),
		);

		// Prepare PM schedules for backend
		const pmSchedules = pmSchedulesForm.map((schedule) => ({
			name: schedule.name,
			description: schedule.description || '',
			categoryId: schedule.categoryId,
			occurrences: schedule.occurrences,
			customDates:
				schedule.occurrences === 'custom' ? schedule.customDates : [],
			startDate: schedule.startDate || null,
			generateDaysBefore: parseInt(schedule.generateDaysBefore) || 30,
			sopId: schedule.sopId,
			serviceReportRequired: schedule.serviceReportRequired,
		}));

		const contractObject = {
			...configurePayload({ ...updatedForm, ...form }, moduleFields?.combined),
			...(form?.document ? { document: form?.document?.value } : {}),
			...{ pmSchedules, notification: notificationForm },
			...(selectedAssetIds?.length > 0 ? { assetIds: selectedAssetIds } : {}),
			...(selectedVendorId ? { vendor: selectedVendorId } : {}),
		};

		handleAddContract(
			{
				...(files?.length
					? {
							files: acceptedFiles,
							bucket: 'local-contracts',
							information: contractObject,
						}
					: contractObject),
			},
			(response) => {
				setAcceptedFiles(null);
				onCloseUploadContractPdfModal();
				setClose();
				if (typeof options?.onSuccess === 'function') {
					options.onSuccess(response);
				} else {
					navigate(`/manage/contracts/${response?.id}`);
				}
			},
			{
				showNotification: {
					success: 'Contract added',
					error: 'Something went wrong...',
				},
			},
		);
	};

	const steps = [
		{ name: 'General' },
		{ name: 'Attributes' },
		{ name: 'Assets' },
		{ name: 'PM Schedules' },
		{ name: 'Vendor' },
	];

	const moduleFieldsBreakdown = {
		general: [
			...(moduleFields?.standard?.filter(
				(field) =>
					field?.isDefault &&
					['name', 'coverage-start', 'coverage-end'].includes(field?.key),
			) || []),
		],
		assets: moduleFields?.standard?.filter(
			(field) => field?.isDefault && ['assets'].includes(field?.key),
		),
		vendor: moduleFields?.standard?.filter(
			(field) => field?.isDefault && ['vendor'].includes(field?.key),
		),
		customAttributes:
			moduleFields?.combined?.filter((field) => !field?.isDefault) || [],
	};

	return (
		<>
			<Modal
				title={
					currentStep === 1
						? 'Select contract type'
						: currentStep === 2
							? 'Upload PDF (optional)'
							: currentStep === 3
								? ''
								: ''
				}
				open={!!options}
				onClose={() => {
					setClose({ onClose: options?.onClose });
				}}
				size={currentStep === 3 ? '4xl' : undefined}
				preventBackdropClose
			>
				{/* 
				
					Step 1: Category Selection 
					
				*/}
				{currentStep === 1 && (
					<div className="grid grid-cols-2 gap-x-4 gap-y-4">
						<div
							onClick={() => {
								setForm({
									...form,
									category: {
										fieldId: CATEGORY_FIELD_ID,
										value: {
											label: SERVICE_CONTRACT_CATEGORY?.name,
											value: SERVICE_CONTRACT_CATEGORY?.id,
										},
									},
								});
								setStep(2);
							}}
							className="border border-gray-200 hover:border-gray-300 cursor-pointer rounded-lg text-xs h-36 flex justify-center items-center font-light text-gray-600 hover:text-gray-900 flex-col"
						>
							<DocumentCheckIcon className="w-6 h-6 text-gray-600 hover:text-gray-900 mb-3" />
							Service Contract
						</div>
						<div
							onClick={() => {
								setForm({
									...form,
									category: {
										fieldId: CATEGORY_FIELD_ID,
										value: {
											label: WARRANTY_CONTRACT_CATEGORY?.name,
											value: WARRANTY_CONTRACT_CATEGORY?.id,
										},
									},
								});
								setStep(2);
							}}
							className="border border-gray-200 hover:border-gray-300 cursor-pointer rounded-lg text-xs h-36 flex justify-center items-center font-light text-gray-600 hover:text-gray-900 flex-col"
						>
							<WrenchScrewdriverIcon className="w-6 h-6 text-gray-600 hover:text-gray-900 mb-3" />
							Warranty
						</div>
						<div className="col-span-2">
							<div className="flex">
								<div className="flex-1">
									<TemplateReactSelect
										name={'or choose a custom contract type'}
										placeholder="Select a custom contract type"
										dataIndex={'categoryId'}
										options={
											categories
												?.filter((category) => !category?.isDefault)
												.map((category) => ({
													label: category?.name,
													value: category?.id,
												})) || []
										}
										value={form['categoryId'] || ''}
										onChange={(index, category) => {
											setForm({
												...form,
												category: {
													fieldId: CATEGORY_FIELD_ID,
													value: category,
												},
											});
											setStep(2);
										}}
										disabled={isLoadingCategories === 'pending'}
									/>
								</div>
								{form['categoryId']?.value ? (
									<div className="flex-shrink flex items-center pt-7 ml-3">
										<XCircleIcon
											className="w-6 h-6 cursor-pointer text-gray-500"
											onClick={() => {
												setForm({ ...form, category: null });
											}}
										/>
									</div>
								) : (
									<div className="flex-shrink flex items-center pt-7 ml-3">
										<PlusCircleIcon
											className="w-6 h-6 cursor-pointer text-gray-500"
											onClick={() =>
												setAddCategoryOpen({
													moduleId: CONTRACT_MODULE_ID,
													onSubmit: (category) => {
														setForm({
															...form,
															category: {
																fieldId: CATEGORY_FIELD_ID,
																value: category,
															},
														});
														setStep(2);
													},
												})
											}
										/>
									</div>
								)}
							</div>
						</div>
					</div>
				)}

				{/* 
				
					Step 2: Document Selection 
					
				*/}
				{currentStep === 2 && (
					<div>
						<p className="text-xs text-gray-500 mb-4">
							If you would like to store the contract PDF provided by the
							vendor, feel free to upload it now. This is optional
						</p>
						<FileUpload
							files={acceptedFiles || []}
							onChange={(files) => {
								if (files.length > 0) {
									const file = files[0];
									if (file.fromLibrary) {
										// Selected from library - open PDF modal with URL
										setAcceptedFiles([file]);
										setForm({
											...form,
											document: { label: file.name, value: file.id },
										});
										onOpenUploadContractPdfModal(true);
									} else {
										// Uploaded file - open PDF modal with File object
										setForm({ ...form, document: null });
										setAcceptedFiles([file]);
										onOpenUploadContractPdfModal(true);
									}
								} else {
									setAcceptedFiles(null);
									setForm({ ...form, document: null });
								}
							}}
							multiple={false}
							showLibraryOption={true}
							libraryFileTypes={['application/pdf']}
							acceptTypes="application/pdf"
							buttonText="Add Contract Document"
							uploadText="Upload PDF"
							libraryText="Choose from Library"
							className="mt-4"
						/>
						<div className="flex justify-end mt-4">
							<Button
								type={
									acceptedFiles?.length || form?.document
										? 'primary'
										: 'secondary'
								}
								onClick={() => {
									onOpenUploadContractPdfModal(true);
								}}
							>
								{acceptedFiles?.length || form?.document
									? 'Continue'
									: 'Skip for now'}
							</Button>
						</div>

						{/* 
				
							Step 2 1/2: PDF Modal Contract Details
					
						*/}

						<PDFModal
							setClose={() => {
								onCloseUploadContractPdfModal();
								setAcceptedFiles(null);
								setForm({ ...form, document: null });
							}}
							options={{
								file: acceptedFiles?.length
									? acceptedFiles[0].fromLibrary
										? acceptedFiles[0].location
										: acceptedFiles[0].previewUrl
									: null,
								uploadComponent: (
									<FileUpload
										files={[]}
										onChange={(files) => {
											if (files.length > 0) {
												const file = files[0];
												if (file.fromLibrary) {
													setAcceptedFiles([file]);
													setForm({
														...form,
														document: { label: file.name, value: file.id },
													});
												} else {
													setForm({ ...form, document: null });
													setAcceptedFiles([file]);
												}
											}
										}}
										multiple={false}
										showLibraryOption={true}
										libraryFileTypes={['application/pdf']}
										acceptTypes="application/pdf"
										buttonText="Add Contract PDF"
									/>
								),
								actions: [
									{
										label: 'Add Contract',
										disabled: !hasAllRequired,
										onClick: () =>
											onAddContract({
												files:
													acceptedFiles?.length > 0
														? acceptedFiles[0]?.fromLibrary
															? []
															: [acceptedFiles[0]?.original].filter(Boolean)
														: [],
											}),
									},
								],
							}}
						>
							<div className="grid gap-y-4">
								<>
									{isLoadingModuleFields === 'pending' ? (
										<LoadingSkeleton />
									) : (
										<>
											<div
												className={classNames(
													'grid gap-y-4 gap-x-4 text-left grid-cols-2',
												)}
											>
												<div className="bg-white border-gray-200 border rounded-lg col-span-2">
													<div className="flex items-center justify-between py-4 px-6 border-b border-gray-200">
														<h3 className="text-sm font-medium text-gray-900 flex items-center">
															General
														</h3>
														<div className="flex items-center space-x-2">
															<Button
																type="ghost"
																size="sm"
																onClick={() =>
																	openManageAttributes({
																		moduleId: CONTRACT_MODULE_ID,
																		onSelect: (id, active) => {
																			onRefreshModuleFields(
																				{
																					categoryId:
																						form?.category?.value?.value,
																				},
																				null,
																				{ silent: true },
																			);
																		},
																	})
																}
															>
																Manage Fields
															</Button>
														</div>
													</div>
													<div className="px-6 py-4 grid grid-cols-2 gap-4">
														<GeneralStepContract
															moduleFieldsBreakdown={moduleFieldsBreakdown}
															skipModuleKeys={skipModuleKeys}
															form={form}
															setForm={setForm}
															updatedForm={updatedForm}
															setUpdateForm={setUpdateForm}
														/>

														<AttributesStepContract
															moduleFieldsBreakdown={moduleFieldsBreakdown}
															skipModuleKeys={skipModuleKeys}
															form={form}
															setForm={setForm}
															updatedForm={updatedForm}
															setUpdateForm={setUpdateForm}
														/>
													</div>
												</div>

												<div className="bg-white border-gray-200 border rounded-lg col-span-2">
													<div className="flex items-center justify-between py-4 px-6 border-b border-gray-200">
														<h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
															Assets
															{autoAddedSections.assets && (
																<span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
																	Automatically Added
																</span>
															)}
														</h3>
													</div>
													<div className="px-6 py-4">
														<AssetSelectorBeta
															value={selectedAssetIds}
															onChange={setSelectedAssetIds}
															assets={processedAssets || []}
															placeholder="Select assets..."
															multiple={true}
															allowGroups={false}
														/>
													</div>
												</div>

												<div className="bg-white border-gray-200 border rounded-lg col-span-2">
													<div className="flex items-center justify-between py-4 px-6 border-b border-gray-200">
														<h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
															PM Schedule
															{autoAddedSections.pmSchedules && (
																<span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
																	Automatically Added
																</span>
															)}
														</h3>
													</div>
													<div className="px-6 py-4">
														<ContractPMSchedules
															pmSchedulesForm={pmSchedulesForm}
															setPMSchedulesForm={setPMSchedulesForm}
															updatedForm={updatedForm}
														/>
													</div>
												</div>

												<div className="bg-white border-gray-200 border rounded-lg col-span-2">
													<div className="flex items-center justify-between py-4 px-6 border-b border-gray-200">
														<h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
															Vendor
															{autoAddedSections.vendor && (
																<span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
																	Automatically Added
																</span>
															)}
														</h3>
													</div>
													<div className="px-6 py-4">
														<VendorSelectorBeta
															value={selectedVendorId}
															onChange={setSelectedVendorId}
															vendors={contacts?.rows || []}
															placeholder="Select a vendor..."
															multiple={false}
															showType={true}
														/>
													</div>
												</div>
											</div>
										</>
									)}
								</>
							</div>
						</PDFModal>
					</div>
				)}

				{/* 
				
					Step 3: Contract Details (Non-modal)
					
				*/}
				{currentStep === 3 && (
					<div className="grid grid-cols-12 gap-x-4">
						{/* 
				
							Step 3.1: Sidebar
							
						*/}
						<div className="col-span-2 my-5">
							<nav aria-label="Progress" className="flex">
								<ol role="list" className="space-y-6">
									{steps.map((step, stepIdx) => (
										<li key={step.name}>
											{stepIdx < currentContractStep ? (
												<a
													href={step.href}
													className="group cursor-pointer"
													onClick={() => setCurrentContractStep(stepIdx)}
												>
													<div className="flex items-center">
														<div
															aria-hidden="true"
															className="relative flex h-5 w-5 flex-shrink-0 items-center justify-center"
														>
															<div className="h-2 w-2 rounded-full bg-gray-300 group-hover:bg-gray-400" />
														</div>
														<p className="ml-3 text-xs font-medium text-gray-500 group-hover:text-gray-900">
															{step.name}
														</p>
													</div>
												</a>
											) : stepIdx === currentContractStep ? (
												<a
													href={step.href}
													aria-current="step"
													className="flex items-center cursor-pointer"
													onClick={() => setCurrentContractStep(stepIdx)}
												>
													<span
														aria-hidden="true"
														className="relative flex h-5 w-5 flex-shrink-0 items-center justify-center"
													>
														<span className="absolute h-4 w-4 rounded-full bg-indigo-200" />
														<span className="relative block h-2 w-2 rounded-full bg-indigo-600" />
													</span>
													<span className="ml-3 text-xs font-medium text-indigo-600">
														{step.name}
													</span>
												</a>
											) : (
												<a
													href={step.href}
													className="group cursor-pointer"
													onClick={() => setCurrentContractStep(stepIdx)}
												>
													<div className="flex items-center">
														<div
															aria-hidden="true"
															className="relative flex h-5 w-5 flex-shrink-0 items-center justify-center"
														>
															<div className="h-2 w-2 rounded-full bg-gray-300 group-hover:bg-gray-400" />
														</div>
														<p className="ml-3 text-xs font-medium text-gray-500 group-hover:text-gray-900">
															{step.name}
														</p>
													</div>
												</a>
											)}
										</li>
									))}
								</ol>
							</nav>
						</div>

						{/* 
				
							Step 3.1: Main Content
							
						*/}
						<div className="col-span-10 pl-4">
							<div className="grid gap-y-4 pt-3">
								<>
									{isLoadingModuleFields === 'pending' ? (
										<LoadingSkeleton />
									) : (
										<>
											<div className="text-lg flex items-center justify-between">
												<div className="flex items-center gap-2">
													{steps[currentContractStep]?.name}
													{currentContractStep === 2 && autoAddedSections.assets && (
														<span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
															Automatically Added
														</span>
													)}
													{currentContractStep === 3 && autoAddedSections.pmSchedules && (
														<span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
															Automatically Added
														</span>
													)}
													{currentContractStep === 4 && autoAddedSections.vendor && (
														<span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
															Automatically Added
														</span>
													)}
												</div>
											</div>
											<div className=" min-h-72">
												<div
													className={classNames(
														'grid gap-y-4 gap-x-4 text-left',
														currentContractStep === 4 ||
															currentContractStep === 3
															? 'grid-cols-1'
															: 'grid-cols-2',
													)}
												>
													{/* 
				
														Step 3.11: Main Content - General Details
														
													*/}
													{currentContractStep === 0 && (
														<GeneralStepContract
															moduleFieldsBreakdown={moduleFieldsBreakdown}
															skipModuleKeys={skipModuleKeys}
															form={form}
															setForm={setForm}
															updatedForm={updatedForm}
															setUpdateForm={setUpdateForm}
														/>
													)}

													{/* 
				
														Step 3.12: Main Content - Attributes
														
													*/}
													{currentContractStep === 1 && (
														<>
															<AttributesStepContract
																moduleFieldsBreakdown={moduleFieldsBreakdown}
																skipModuleKeys={skipModuleKeys}
																form={form}
																setForm={setForm}
																updatedForm={updatedForm}
																setUpdateForm={setUpdateForm}
															/>
														</>
													)}

													{/* 
				
														Step 3.13: Main Content - Assets
														
													*/}

													{currentContractStep === 2 && (
														<div className="col-span-2 space-y-4">
															<AssetSelectorBeta
																value={selectedAssetIds}
																onChange={setSelectedAssetIds}
																assets={assets?.rows || []}
																placeholder="Select assets..."
																multiple={true}
																allowGroups={true}
															/>
															<SelectedAssetsList
																assets={(assets?.rows || []).filter((a) =>
																	selectedAssetIds.includes(a.id),
																)}
																onRemove={(assetId) =>
																	setSelectedAssetIds(
																		selectedAssetIds.filter(
																			(id) => id !== assetId,
																		),
																	)
																}
															/>
														</div>
													)}

													{/*

														Step 3.14: Main Content - PM Schedules

													*/}
													{currentContractStep === 3 && (
														<>
															<div className="col-span-2 space-y-4">
																<ContractPMSchedules
																	pmSchedulesForm={pmSchedulesForm}
																	setPMSchedulesForm={setPMSchedulesForm}
																	updatedForm={updatedForm}
																/>
															</div>
														</>
													)}

													{/* 
				
														Step 3.15: Main Content - Vendor Details
														
													*/}
													{currentContractStep === 4 && (
													<div className="col-span-2">
														<VendorSelectorBeta
															value={selectedVendorId}
															onChange={setSelectedVendorId}
															vendors={contacts?.rows || []}
															placeholder="Select a vendor..."
															multiple={false}
															showType={true}
														/>
													</div>
												)}
												</div>
											</div>
										</>
									)}
								</>

								<div className="flex justify-end mt-4">
									{currentContractStep + 1 !== steps?.length && (
										<Button
											onClick={() => {
												setCurrentContractStep(currentContractStep + 1);
											}}
											type="ghost"
										>
											{`Optional: Add ${steps[currentContractStep + 1]?.name}`}
										</Button>
									)}

									<Button
										disabled={!hasAllRequired}
										onClick={() =>
											onAddContract({
												files:
													acceptedFiles?.length > 0 &&
													!acceptedFiles[0]?.fromLibrary
														? [acceptedFiles[0]?.original].filter(Boolean)
														: [],
											})
										}
									>
										Add Contract
									</Button>
								</div>
							</div>
						</div>
					</div>
				)}
			</Modal>

			<Modal
				title="Add custom field"
				open={isAddCustomAttributesModalOpen}
				onClose={() => setAddCustomAttributesModal(false)}
				size="xl"
			>
				<>
					{isLoadingModuleFields === 'pending' ? (
						<LoadingSkeleton />
					) : (
						<>
							<div className="grid grid-cols-1 gap-y-4 gap-x-4 text-left">
								<TemplateCreateable
									name="Attribute"
									placeholder="Select or type to create..."
									options={moduleFields?.standard
										?.filter((field) => !field?.isDefault)
										.map((field) => ({
											label: field?.label,
											value: field?.id,
										}))}
									onChange={(index, selectedCustomField, raw) => {
										if (raw) {
											setCustomAttributeForm({
												...customAttributeForm,
												...(raw?.__isNew__
													? { label: raw?.label }
													: { id: raw?.value }),
											});
										} else {
											setCustomAttributeForm({});
										}
									}}
									skip={[]}
									value={customAttributeForm?.label || customAttributeForm?.id}
								/>

								{customAttributeForm?.label ? (
									<div className="grid grid-cols-2 gap-y-4">
										<TemplateReactSelect
											name="Type"
											options={FIELD_OPTIONS}
											onChange={(index, type) =>
												setCustomAttributeForm({ ...customAttributeForm, type })
											}
											value={customAttributeForm?.type}
										/>
										<TemplateBoolean
											span={'sm:col-span-1'}
											name="Required"
											className="mr-3"
											onChange={(index, required) =>
												setCustomAttributeForm({
													...customAttributeForm,
													required,
												})
											}
											value={customAttributeForm?.required}
										/>
										<TemplateBoolean
											span={'sm:col-span-1'}
											name="Searchable"
											onChange={(index, searchable) =>
												setCustomAttributeForm({
													...customAttributeForm,
													searchable,
												})
											}
											value={customAttributeForm?.searchable}
										/>

										<TemplateField
											span={'sm:col-span-2'}
											name="Value"
											className="mr-3"
											onChange={(index, value) =>
												setCustomAttributeForm({
													...customAttributeForm,
													value,
												})
											}
											value={customAttributeForm?.value}
										/>
									</div>
								) : null}
							</div>

							<div className="col-span-2 flex justify-end mt-8">
								<Button onClick={() => setAddCustomAttributesModal(false)}>
									Done
								</Button>
							</div>
						</>
					)}
				</>
			</Modal>
		</>
	);
};

export default AddContractBetaModal;
